// src/ContactUsPage.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Drawer,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Avatar,
  useTheme,
  Stack,
  Badge,
} from '@mui/material';
import FilterListIcon       from '@mui/icons-material/FilterList';
import SortIcon             from '@mui/icons-material/Sort';
import AddIcon              from '@mui/icons-material/Add';
import RefreshIcon          from '@mui/icons-material/Refresh';
import ConfirmationNumber   from '@mui/icons-material/ConfirmationNumber';
import WarningAmberIcon     from '@mui/icons-material/WarningAmber';
import PendingActionsIcon   from '@mui/icons-material/PendingActions';
import CheckCircleOutline   from '@mui/icons-material/CheckCircleOutline';
import { useMsal }          from '@azure/msal-react';
import { supabase }         from './supabaseClient';
import ProfileDialog        from './ProfileDialog';
import { normalizeProfileImage } from './utils/imageUtils';

import { brand, ink, status, surface } from './designTokens';
import { prettyStatus } from './labels';
const VIEW_NAME         = 'Contact Us';
const STATUS_OPTIONS    = ['Open','Pending','Resolved','Closed'];
const PRIORITY_OPTIONS  = ['All','High','Medium','Low'];

// color maps
const statusColorMap = {
  Open:     status.dangerBg,
  Pending:  status.warningBg,
  Resolved: status.successBg,
  Closed:   status.successBg,
};
const priorityColorMap = {
  High:   status.dangerBg,
  Medium: status.warningBg,
  Low:    surface.line,
};
// ranking for sorting
const statusRank   = { Open:0, Pending:1, Resolved:2, Closed:2 };

// How long an open ticket has been waiting.
//
// A queue that shows status but not age cannot tell you that something has sat
// Open for nine days, which is the only thing about it that has changed.
// Closed and resolved tickets show nothing — their age is not a problem.
const ticketAge = (row) => {
  if (['Resolved', 'Closed'].includes(row.status)) return null;
  const created = row.created_at ? new Date(row.created_at) : null;
  if (!created || Number.isNaN(created.getTime())) return null;
  const hours = Math.floor((Date.now() - created.getTime()) / 36e5);
  if (hours < 1)  return { label: 'just now', tone: ink[500] };
  if (hours < 24) return { label: `${hours}h waiting`, tone: ink[500] };
  const days = Math.floor(hours / 24);
  return {
    label: `${days}d waiting`,
    // Three days is where a support reply stops reading as a reply and starts
    // reading as an apology.
    tone: days >= 3 ? '#B3261E' : days >= 1 ? '#A87D35' : ink[500],
  };
};
const priorityRank = { High:0, Medium:1, Low:2 };

/// Email comparison, case-insensitively — Microsoft is inconsistent about it.
const sameAdmin = (a, b) =>
  Boolean(a) && Boolean(b) && String(a).toLowerCase() === String(b).toLowerCase();

/// "first.last@example.com" -> "First".
///
/// The example here is deliberately not a real address. OWNER_EMAILS is marked
/// as a secret value in Netlify, and the scanner searches the repo for that
/// value — so a real address in a comment fails every deploy.
/// A last resort when neither admin_users nor Microsoft supplies a name, and
/// still a person's name rather than the word "Unknown".
const prettyNameFromEmail = (email='') => {
  const local = String(email).split('@')[0] || '';
  const words = local.split(/[._-]+/).filter(Boolean);
  if (words.length === 0) return 'Support';
  return words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

export default function ContactUsPage() {
  const theme = useTheme();
  const { accounts } = useMsal();
  const adminEmail = accounts[0]?.username || accounts[0]?.idTokenClaims?.email || '';

  // Never "Unknown".
  //
  // This was `accounts[0]?.name || 'Unknown'`, and Microsoft does not always
  // return a name — so ticket_comments holds rows literally authored by
  // "Unknown", which is what the customer saw signing the reply to their
  // problem. admin_users is asked first (it is where a real display name
  // belongs), then the Microsoft account, and failing both the email's local
  // part is tidied into something like "Samantha". Every rung of that ladder
  // names a person; "Unknown" names nobody.
  const [adminName, setAdminName] = useState(() => accounts[0]?.name || prettyNameFromEmail(adminEmail));
  const currentUser = adminName;

  useEffect(() => {
    if (!adminEmail) return;
    (async () => {
      const { data } = await supabase
        .from('admin_users')
        .select('first_name,last_name')
        .ilike('microsoft_email', adminEmail)
        .maybeSingle();
      const full = `${data?.first_name || ''} ${data?.last_name || ''}`.trim();
      if (full) setAdminName(full);
    })();
  }, [adminEmail]);

  const [rows,           setRows]           = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [snackbar,       setSnackbar]       = useState({ open:false, message:'', severity:'success' });
  const [detailRow,      setDetailRow]      = useState(null);
  const [client,         setClient]         = useState(null);
  const [comments,       setComments]       = useState([]);
  const [clientResolved, setClientResolved] = useState(false);
  const [unread,         setUnread]         = useState({});
  const [newComment,     setNewComment]     = useState('');
  const [profilePhone,   setProfilePhone]   = useState(null);
  const [booking,        setBooking]        = useState(null);
  const [filterText,     setFilterText]     = useState('');
  const [priorityFilter, setPriorityFilter] = useState('All');

  // The conversation scroller. A taller thread box makes the opposite problem
  // visible: it opens at the oldest message, so on a long ticket the newest
  // reply — the one being answered — is the one off-screen.
  const threadRef = useRef(null);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [comments]);

  const enrichRowsWithProfiles = useCallback(async (rawRows = []) => {
    const phones = [...new Set(rawRows.map(r => r.phone_number).filter(Boolean))];
    if (phones.length === 0) return rawRows;
    const { data: profiles } = await supabase
      .from('Profiles')
      .select('first_name,last_name,email,phone_number,profile_image');
    const map = new Map();
    (profiles || []).forEach(p => {
      map.set(p.phone_number, { 
        ...p, 
        profile_image_url: normalizeProfileImage(p.profile_image),
      });
    });
    return rawRows.map(r => {
      const prof = map.get(r.phone_number);
      return {
        ...r,
        avatarUrl: prof?.profile_image_url || null,
        userName: prof ? `${prof.first_name || ''} ${prof.last_name || ''}`.trim() : null,
      };
    });
  }, []);

  // 1) fetch tickets
  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from(VIEW_NAME)
        .select('*')
        .order('created_at',{ ascending:false });
      if (!isMounted) return;
      setLoading(false);
      if (error) {
        setSnackbar({ open:true, message:error.message, severity:'error' });
      } else {
        const enriched = await enrichRowsWithProfiles(data || []);
        if (isMounted) setRows(enriched);
      }
    })();
    return () => { isMounted = false; };
  }, [enrichRowsWithProfiles]);

  // Live, both in the list and in the open thread.
  //
  // ticket_comments is published to supabase_realtime, so a customer replying
  // while an agent has the queue open no longer sits unseen until someone
  // reloads. Two effects, because they answer different questions: is this
  // thread current, and which tickets have something new.
  useEffect(() => {
    const channel = supabase
      .channel('contact-us-comments')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'ticket_comments' },
        (payload) => {
          const ticketId = payload?.new?.ticket_id;
          if (ticketId == null) return;

          // Refresh the thread if it is the one on screen.
          setDetailRow((current) => {
            if (current && current.id === ticketId) {
              supabase
                .from('ticket_comments')
                .select('id,ticket_id,author,content,created_at')
                .eq('ticket_id', ticketId)
                .order('created_at', { ascending: true })
                .then(({ data }) => { if (data) setComments(data); });
            }
            return current;
          });

          // Flag it in the list. Support's own replies are not news to support,
          // so only a reply from someone else counts as unread.
          const author = String(payload?.new?.author ?? '');
          setUnread((prev) => {
            const mine = author === currentUser;
            if (mine) return prev;
            return { ...prev, [ticketId]: true };
          });
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [currentUser]);

  // Opening a ticket clears its flag.
  useEffect(() => {
    if (!detailRow) return;
    setUnread((prev) => {
      if (!prev[detailRow.id]) return prev;
      const next = { ...prev };
      delete next[detailRow.id];
      return next;
    });
  }, [detailRow]);

  // 2) load client + comments when detailRow
  useEffect(() => {
    if (!detailRow) {
      setClient(null);
      setComments([]);
      setBooking(null);
      return;
    }
    // Reports filed from a booking's Help menu carry booking_id. Resolve it so
    // the panel shows the actual session rather than a bare uuid.
    setBooking(null);
    if (detailRow.booking_id) {
      supabase
        .from('bookings')
        .select('id,service_title,start_ts,status,price,payment_completed,stylists_phone_number')
        .eq('id', detailRow.booking_id)
        .maybeSingle()
        .then(({ data, error }) => { if (data && !error) setBooking(data); });
    }
    // client
    // Digits-only match. "Contact Us".phone_number is hand-entered and
    // unnormalised — ticket 9 holds "+1 9173797004" with a space — so .eq
    // matched nothing, `client` stayed null, and the panel spun forever while
    // never showing a name.
    //
    // `clientResolved` is set either way. A ticket raised by someone with no
    // profile is a real case (email-only enquiries), and it must fall back to
    // the ticket's own details rather than spin.
    setClientResolved(false);
    supabase
      .rpc('profile_by_phone', { p_phone: detailRow.phone_number || '' })
      .then(({ data, error }) => {
        const found = Array.isArray(data) ? data[0] : data;
        if (found && !error) {
          found.avatarUrl = normalizeProfileImage(found.profile_image);
          setClient(found);
        } else {
          setClient(null);
        }
        setClientResolved(true);
      });
    // comments
    supabase
      .from('ticket_comments')
      .select('id,ticket_id,author,content,created_at')
      .eq('ticket_id', detailRow.id)
      .order('created_at',{ ascending:true })
      .then(({ data, error }) => {
        if (!error) setComments(data);
      });
  }, [detailRow]);

  // 3) inline status change + stamp closed_at
  const updateStatus = async (id, newStatus) => {
    const changes = { status: newStatus };
    if (newStatus==='Resolved' || newStatus==='Closed') {
      changes.closed_at = new Date().toISOString();
    }
    const { error } = await supabase
      .from(VIEW_NAME)
      .update(changes)
      .eq('id', id);
    if (error) {
      console.error(error);
      setSnackbar({ open:true, message:'Status update failed', severity:'error' });
    } else {
      setRows(rs => rs.map(r => r.id===id ? { ...r, ...changes } : r));
      setDetailRow(r => r?.id===id ? { ...r, ...changes } : r);
    }
  };

  /// Take a ticket, or find out who already has it.
  ///
  /// Returns false when someone else holds it and the caller has not chosen to
  /// take over, so a reply can be stopped before it is written into a
  /// conversation another admin is already having.
  const claimTicket = async (ticket, { takeOver = false } = {}) => {
    if (!adminEmail) return true;   // no identity to claim with; don't block the reply
    const { data, error } = await supabase.rpc('claim_ticket', {
      p_ticket_id: ticket.id,
      p_email:     adminEmail,
      p_name:      currentUser,
      p_take_over: takeOver,
    });
    if (error) {
      console.warn('claim_ticket failed', error);
      return true;                  // never let bookkeeping block answering a customer
    }
    const result = Array.isArray(data) ? data[0] : data;
    if (result?.was_already_assigned) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: `Ticket #${ticket.id} is assigned to ${result.assigned_admin_name || result.assigned_admin_email}. Use "Take over" to reply anyway.`,
      });
      const held = {
        assigned_admin_email: result.assigned_admin_email,
        assigned_admin_name:  result.assigned_admin_name,
      };
      setRows(rs => rs.map(r => r.id === ticket.id ? { ...r, ...held } : r));
      setDetailRow(r => r?.id === ticket.id ? { ...r, ...held } : r);
      return false;
    }
    const mine = {
      assigned_admin_email: adminEmail,
      assigned_admin_name:  currentUser,
    };
    setRows(rs => rs.map(r => r.id === ticket.id ? { ...r, ...mine } : r));
    setDetailRow(r => r?.id === ticket.id ? { ...r, ...mine } : r);
    return true;
  };

  // 4) post comment
  const handleAddComment = async ({ takeOver = false } = {}) => {
    if (!newComment.trim() || !detailRow) return;

    // Claim before writing. A ticket belongs to one admin, and the moment that
    // is decided is the moment someone answers it — the customer is about to be
    // told a name, and it should be the name that stays on the conversation.
    const claimed = await claimTicket(detailRow, { takeOver });
    if (!claimed) return;

    const entry = {
      ticket_id:  detailRow.id,
      author:     currentUser,
      content:    newComment.trim(),
      created_at: new Date().toISOString(),
    };
    const { error } = await supabase.from('ticket_comments').insert(entry);
    if (error) {
      console.error(error);
      setSnackbar({ open:true, message:'Comment save failed', severity:'error' });
    } else {
      setComments(cs => [...cs, entry]);
      setNewComment('');

      // Tell the customer their ticket was answered. Deliberately after the
      // insert and deliberately not awaited into the error path: the reply is
      // already saved, and a push that fails must not read to support staff as
      // a reply that failed. Worst case they are told the notification did not
      // go out, which is true and actionable.
      supabase.functions
        .invoke('notify-ticket-reply', {
          body: { ticket_id: detailRow.id, preview: entry.content },
        })
        .then(({ error: pushError }) => {
          if (pushError) {
            console.warn('ticket reply push failed', pushError);
            setSnackbar({
              open: true,
              message: 'Reply saved, but the customer could not be notified',
              severity: 'warning',
            });
          }
        })
        .catch((e) => console.warn('ticket reply push failed', e));
    }
  };

  // 5) filter & sort
  const displayed = rows
    .filter(r =>
      (!filterText ||
       [r.email, r.phone_number, r.subject].some(f =>
         f?.toLowerCase().includes(filterText.toLowerCase())
       ))
      &&
      (priorityFilter==='All' || r.priority===priorityFilter)
    )
  .sort((a, b) => {
    // status first, then priority
    const sr = statusRank[a.status] - statusRank[b.status];
    if (sr !== 0) return sr;
    return priorityRank[a.priority] - priorityRank[b.priority];
  });

  const stop = e => e.stopPropagation();

  return (
    <Box sx={{ p:2 }}>
      {/* Header + Refresh */}
      <Box sx={{ display:'flex', alignItems:'center', mb:3 }}>
        <Typography variant="h4" sx={{ flexGrow:1 }}>
          Customer Support Tickets
        </Typography>
        <IconButton onClick={()=>window.location.reload()}>
          <RefreshIcon/>
        </IconButton>
      </Box>

      {/* KPI cards */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p:2, display:'flex', alignItems:'center', gap:1 }}>
            <ConfirmationNumber fontSize="small"/>
            <Box>
              <Typography variant="caption">Open Tickets</Typography>
              <Typography variant="h6">
                {rows.filter(r=>r.status==='Open').length}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p:2, display:'flex', alignItems:'center', gap:1 }}>
            <WarningAmberIcon fontSize="small" sx={{ color:'red' }}/>
            <Box>
              <Typography variant="caption">Urgent</Typography>
              <Typography variant="h6">
                {rows.filter(r=>r.priority==='High').length}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p:2, display:'flex', alignItems:'center', gap:1 }}>
            <PendingActionsIcon fontSize="small"/>
            <Box>
              <Typography variant="caption">Pending</Typography>
              <Typography variant="h6">
                {rows.filter(r=>r.status==='Pending').length}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p:2, display:'flex', alignItems:'center', gap:1 }}>
            <CheckCircleOutline fontSize="small" sx={{ color:'green' }}/>
            <Box>
              <Typography variant="caption">Resolved Today</Typography>
              <Typography variant="h6">
                {rows.filter(r=>{
                  if (!r.closed_at) return false;
                  return new Date(r.closed_at).toDateString() === new Date().toDateString();
                }).length}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ display:'flex', alignItems:'center', mb:2, gap:1, flexWrap:'wrap' }}>
        <TextField
          placeholder="Search…"
          size="small"
          value={filterText}
          onChange={e=>setFilterText(e.target.value)}
          sx={{ width:250 }}
        />
        <Select
          size="small"
          value={priorityFilter}
          onChange={e=>setPriorityFilter(e.target.value)}
        >
          {PRIORITY_OPTIONS.map(p=>(
            <MenuItem key={p} value={p}>{p}</MenuItem>
          ))}
        </Select>
        <Button startIcon={<FilterListIcon/>}>Filter</Button>
        <Button startIcon={<SortIcon/>}>Sort</Button>
        <Button variant="contained" startIcon={<AddIcon/>}>New Ticket</Button>
      </Box>

      {/* Ticket table */}
      {loading
        ? <Box sx={{ textAlign:'center', p:4 }}><CircularProgress/></Box>
        : (
          <TableContainer component={Paper}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {/* No "About" column. It holds the customer's full opening
                      message, which is prose — it either squeezed every other
                      column out of the way or got clipped to a useless
                      fragment. It reads properly in View More, where there is
                      room for it, and the row keeps Subject for the gist. */}
                  {['User','Email','Phone','Type','Subject','Created','Priority','Status','Closed',''].map((h,i)=>(
                    <TableCell key={i} sx={{ textTransform:'capitalize' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.map(row=>(
                  <TableRow
                    key={row.id}
                    hover
                    onClick={()=>setDetailRow(row)}
                    sx={{ cursor:'pointer' }}
                  >
                    <TableCell onClick={stop}>
                      <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                        <Badge
                          color="error"
                          variant="dot"
                          overlap="circular"
                          invisible={!unread[row.id]}
                          anchorOrigin={{ vertical:'top', horizontal:'right' }}
                        >
                        <Avatar sx={{ width: 32, height: 32, bgcolor: ink[900], fontSize: '0.85rem' }} src={row.avatarUrl || undefined}>
                          {row.userName?.[0] || row.email?.[0] || '?'}
                        </Avatar>
                        </Badge>
                        {/* Name only. The phone number was repeated here as a
                            caption and again in the Phone column immediately
                            to the right, where it is also the link into the
                            profile. */}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.userName || row.email || 'Unknown'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell onClick={stop}>{row.email}</TableCell>
                    <TableCell onClick={e=>{
                      stop(e);
                      setProfilePhone(row.phone_number);
                    }}>
                      <Button
                        size="small"
                        variant="text"
                        sx={{ p:0, textTransform:'none', fontSize:'0.75rem' }}
                      >
                        {row.phone_number}
                      </Button>
                    </TableCell>
                    <TableCell onClick={stop}>
                      {row.booking_id
                        ? <Chip
                            size="small"
                            label={row.category || 'Booking issue'}
                            sx={{ backgroundColor: status.warningBg, color: ink[900], fontWeight: 600 }}
                          />
                        : <Typography variant="caption" sx={{ color: ink[500] }}>Message</Typography>}
                    </TableCell>
                    <TableCell onClick={stop}>{row.subject}</TableCell>
                    <TableCell onClick={stop}>
                      {new Date(row.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell onClick={stop}>
                      <Chip
                        label={row.priority}
                        size="small"
                        sx={{
                          backgroundColor: priorityColorMap[row.priority],
                          color: theme.palette.text.primary,
                          fontSize:'0.75rem',
                          height:24
                        }}
                      />
                    </TableCell>
                    <TableCell onClick={stop}>
                      {(() => {
                        const age = ticketAge(row);
                        return age ? (
                          <Typography
                            variant="caption"
                            sx={{ color: age.tone, display: 'block', mb: 0.5, fontWeight: 600 }}
                          >
                            {age.label}
                          </Typography>
                        ) : null;
                      })()}
                      <Select
                        value={row.status}
                        size="small"
                        onChange={e=>updateStatus(row.id,e.target.value)}
                        sx={{
                          backgroundColor: statusColorMap[row.status],
                          color: theme.palette.text.primary,
                          '& .MuiSelect-icon':{ color: theme.palette.text.primary },
                          borderRadius:1, px:1,
                          fontSize:'0.75rem',
                          height:24,
                          minWidth:64
                        }}
                      >
                        {STATUS_OPTIONS.map(s=>(
                          <MenuItem
                            key={s}
                            value={s}
                            sx={{
                              backgroundColor: statusColorMap[s],
                              color: theme.palette.text.primary,
                              fontSize:'0.75rem'
                            }}
                          >{s}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell onClick={stop}>
                      {row.closed_at
                        ? new Date(row.closed_at).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell onClick={stop} sx={{ whiteSpace:'nowrap' }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={()=>setDetailRow(row)}
                        sx={{
                          p:0,
                          minWidth:0,
                          fontSize:'0.75rem',
                          textTransform:'none',
                          lineHeight:1
                        }}
                      >
                        View More
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      }

      {/* Detail drawer */}
      <Drawer
        anchor="right"
        open={Boolean(detailRow)}
        onClose={()=>setDetailRow(null)}
        PaperProps={{ sx:{
          width:{ xs:'100%', sm:520, lg:620 },
          p:2,
          // A column, so the conversation can be given the leftover height
          // instead of a fixed 240px box. See the Comments block below.
          display:'flex',
          flexDirection:'column',
        } }}
      >
        {detailRow && (
          <>
            {/* Client info */}
            {!clientResolved ? (
              <Box sx={{ textAlign:'center', py:2 }}>
                <CircularProgress size={24}/>
              </Box>
            ) : (
              <Box sx={{ display:'flex', alignItems:'center', mb:2, flexShrink:0 }}>
                <Avatar src={client?.avatarUrl} sx={{ width:56, height:56, mr:2 }}>
                  {(client?.first_name || detailRow.name || '?')[0]}
                </Avatar>
                <Box sx={{ minWidth:0 }}>
                  {/* The ticket's own name is the fallback: someone who wrote in
                      without an account still has a name on their enquiry. */}
                  <Typography variant="subtitle1">
                    {client ? `${client.first_name || ''} ${client.last_name || ''}`.trim()
                            : (detailRow.name || 'Unknown customer')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {client?.email || detailRow.email || '—'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {client?.phone_number || detailRow.phone_number || '—'}
                  </Typography>
                  {!client && (
                    <Typography variant="caption" sx={{ color: ink[500] }}>
                      No matching account
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            <Divider/>

            {/* Ticket details.
                Capped and scrollable so a long Description — now that it lives
                only here, off the row — can't push the conversation off the
                bottom of the drawer. */}
            <Box sx={{ mt:2, flexShrink:0, maxHeight:'30vh', overflowY:'auto' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb:1 }}>
                <Typography variant="h6">Ticket #{detailRow.id}</Typography>
                {/* Urgency as a chip, not a row in a label list. It is the
                    thing that decides whether this gets answered now, and it
                    was buried between "Created" and "Status". */}
                {detailRow.priority && (
                  <Chip
                    size="small"
                    label={String(detailRow.priority).toUpperCase()}
                    sx={{
                      fontWeight:700,
                      bgcolor: /urgent|high/i.test(detailRow.priority) ? '#B3261E'
                             : /medium|normal/i.test(detailRow.priority) ? brand.gold
                             : surface.sunken,
                      color: /urgent|high|medium|normal/i.test(detailRow.priority) ? '#fff' : ink[700],
                    }}
                  />
                )}
                {(() => {
                  const age = ticketAge(detailRow);
                  return age ? (
                    <Typography variant="caption" sx={{ color: age.tone, fontWeight:600 }}>
                      {age.label}
                    </Typography>
                  ) : null;
                })()}
              </Stack>
              {[
                ['Subject',     detailRow.subject],
                ['Description', detailRow.about],
                ['Created',     new Date(detailRow.created_at).toLocaleString()],
                ['Priority',    detailRow.priority],
                ['Status',      detailRow.status],
                ['Closed At',   detailRow.closed_at
                                  ? new Date(detailRow.closed_at).toLocaleString()
                                  : '—'],
                ...(detailRow.category ? [['Category', detailRow.category]] : []),
                ...(detailRow.booking_id
                      ? [['Booking', booking
                            ? `${booking.service_title || 'Session'} · ${booking.start_ts ? new Date(booking.start_ts).toLocaleString() : 'no date'} · ${prettyStatus(booking.status)}${booking.payment_completed ? '' : ' · unpaid'}`
                            : detailRow.booking_id]]
                      : []),
              ].map(([label,val])=>(
                <Box key={label} sx={{ mb:1 }}>
                  <Typography variant="subtitle2">{label}</Typography>
                  <Typography variant="body2">{val}</Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my:2 }}/>

            {/* Who is handling this.
                Shown above the conversation because it decides whether you
                should be typing in it at all, and because it is the name the
                customer sees on every reply. */}
            <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1, flexShrink:0 }}>
              <Typography variant="caption" sx={{ color: ink[500] }}>Assigned to</Typography>
              {detailRow.assigned_admin_email ? (
                <>
                  <Chip
                    size="small"
                    label={detailRow.assigned_admin_name || detailRow.assigned_admin_email}
                    sx={{
                      fontWeight: 600,
                      bgcolor: sameAdmin(detailRow.assigned_admin_email, adminEmail) ? brand.gold : surface.sunken,
                      color:   sameAdmin(detailRow.assigned_admin_email, adminEmail) ? '#fff' : ink[900],
                    }}
                  />
                  {!sameAdmin(detailRow.assigned_admin_email, adminEmail) && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => claimTicket(detailRow, { takeOver: true })}
                      sx={{ textTransform:'none' }}
                    >
                      Take over
                    </Button>
                  )}
                </>
              ) : (
                <Typography variant="caption" sx={{ color: ink[500], fontStyle:'italic' }}>
                  nobody yet — replying assigns it to you
                </Typography>
              )}
            </Box>

            {/* Comments */}
            <Typography variant="subtitle1" gutterBottom sx={{ flexShrink:0 }}>Comments</Typography>
            {/* Takes whatever height is left in the drawer rather than a fixed
                240px, which on any normal screen showed about two messages and
                made a conversation of ten feel like reading through a letterbox.
                minHeight keeps it usable if the details above run long. */}
            <Box
              ref={threadRef}
              sx={{ flex:1, minHeight:260, overflowY:'auto', mb:2, pr:0.5 }}
            >
              {/* A conversation, not a log.
                  Support on the right in gold, the customer on the left in
                  grey — the shape everyone already reads from every messaging
                  app, and the shape the customer sees on their side. Author and
                  time sit under the bubble so the eye follows the words first. */}
              {comments.map(c=>{
                const fromSupport = c.author !== detailRow?.name;
                return (
                  <Box
                    key={c.id||c.created_at}
                    sx={{
                      display:'flex',
                      justifyContent: fromSupport ? 'flex-end' : 'flex-start',
                      mb:1.5,
                    }}
                  >
                    <Box sx={{ maxWidth:'78%' }}>
                      <Box sx={{
                        px:1.5, py:1,
                        borderRadius: fromSupport
                          ? '12px 12px 2px 12px'
                          : '12px 12px 12px 2px',
                        bgcolor: fromSupport ? brand.gold : surface.sunken,
                        color: fromSupport ? '#fff' : ink[900],
                      }}>
                        <Typography variant="body2" sx={{ fontSize:'0.875rem', whiteSpace:'pre-wrap' }}>
                          {c.content}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: ink[500], display:'block', mt:0.4,
                          textAlign: fromSupport ? 'right' : 'left',
                        }}
                      >
                        {fromSupport ? c.author : (detailRow?.name || 'Customer')}
                        {' · '}
                        {new Date(c.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              {comments.length === 0 && (
                <Typography variant="body2" sx={{ color: ink[500], textAlign:'center', py:2 }}>
                  No replies yet. The customer is notified as soon as you send one.
                </Typography>
              )}
            </Box>

            {/* New comment */}
            <Box sx={{ display:'flex', gap:1, flexShrink:0 }}>
              <TextField
                fullWidth
                placeholder="Add comment…"
                size="small"
                value={newComment}
                onChange={e=>setNewComment(e.target.value)}
              />
              <Button variant="contained" size="small" onClick={handleAddComment}>
                Post
              </Button>
            </Box>
          </>
        )}
      </Drawer>

      {/* ProfileDialog */}
      <ProfileDialog
        open={Boolean(profilePhone)}
        phoneNumber={profilePhone}
        onClose={()=>setProfilePhone(null)}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={()=>setSnackbar(s=>({...s,open:false}))}
        anchorOrigin={{ vertical:'bottom', horizontal:'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width:'100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
